import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import './GameEndModal.css';
import GameEndContent from "../../GameEndContent/GameEndContent";
import {PRIMARY_COLOR} from "../../../styles/constants";
import {
  hideGameEndModal,
  leaveLobby,
  resetState,
} from "../../../redux/actions";
import {
  selectIsFieldCutscenePlaying,
  selectIsTutorialActive,
  selectShowGameEndModal
} from "../../../redux/reducers/privateStateReducer/selectors";
import {
  selectIsCityCaptureModalVisible,
  selectIsCombatResultsModalVisible
} from "../../../redux/selectors/modalVisibility";
import {
  selectExtendedGameResults,
  selectFinalRound,
  selectGameEndReason,
  selectGameEndRound,
  selectGameResults,
  selectGameStartedAt,
  selectGameType,
  selectIsRankedMatch,
  selectPlayers,
} from "../../../redux/reducers/sharedStateReducer/selectors";
import RankSkinRewardModal from "../RankSkinRewardModal/RankSkinRewardModal";
import ModalWindow from "../ModalWindow/ModalWindow";
import {useEffectOnPositiveChange} from "../../../hooks/react/useEffectOnPositiveChange";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import ModalButton from "../ModalWindow/ModalButton/ModalButton";
import {closeNetworkSocket} from "../../../utils/networkSocket";
import {SCREENS} from "../../../constants/screens";
import {GAME_END_REASONS, GAME_TYPES} from "../../../constants/game";
import {GAME_END_WIN_POINTS_GAP} from "../../../configs/game";
import {pluralizeRu} from "../../../utils/other/pluralizeRu";
import MatchWinnerModal from "../MatchWinnerModal/MatchWinnerModal";
import {getLocalMatchReplayRecorder} from "../../../utils/tutorial/replay/tutorialReplayRecorderRegistry";
import {createDefaultRunId} from "../../../utils/tutorial/replay/createTutorialReplayRecorder";
import {saveLocalMatchResults} from "../../../utils/localMatch/saveLocalMatchResults";
import {applyLocalMatchSaveResult} from "../../../utils/localMatch/applyLocalMatchSaveResult";
import FactionPurchaseRewardModal from "../FactionPurchaseRewardModal/FactionPurchaseRewardModal";
import QuestChestRewardModal from "../QuestChestRewardModal/QuestChestRewardModal";
import {setAvailableFactions, updatePlayerRewards} from "../../../redux/actions";

// Пауза между появлением итогов матча и наградной модалкой поверх них
const RANK_SKIN_MODAL_DELAY_MS = 800;

const GameEndModal = ({setScreen, handleRankedGame}) => {
  const dispatch = useDispatch();
  // Лобби есть только у участников матча; при просмотре ЗАПИСИ его нет,
  // поэтому признак дублируется в состоянии партии (см. startGame)
  const lobbyIsRanked = useSelector((state) => state.private.lobby?.isRanked);
  const matchIsRanked = useSelector(selectIsRankedMatch);
  const isRanked = Boolean(lobbyIsRanked || matchIsRanked);
  // Гость смотрел матч зрителем: рейтинговый поиск ему без аккаунта недоступен —
  // «Снова в битву!» ведет в список матчей, как после обычного матча
  const isGuest = useSelector((state) => Boolean(state.private.auth?.isGuest));
  const modalShown = useSelector(selectShowGameEndModal);
  const isTutorialActive = useSelector(selectIsTutorialActive);
  // Именно ВИДИМЫЕ модалки, а не поднятые флаги их показа: боевые модалки
  // сами исчезают с экрана, когда их данные перезаписывает следующий расчет
  // боя или полное состояние с сервера, — а флаг при этом остается поднятым.
  // Раньше такой невидимый флаг навсегда прятал итоги матча (см.
  // redux/selectors/modalVisibility.js)
  const cityCaptureModalVisible = useSelector(selectIsCityCaptureModalVisible);
  // Захват последнего раунда сначала показывается облетом на поле (см.
  // scenes/CityCaptureAnimation), и итоги ждут его, как и модалку захвата
  const isFieldCutscenePlaying = useSelector(selectIsFieldCutscenePlaying);
  const combatResultsModalVisible = useSelector(selectIsCombatResultsModalVisible);
  const auth = useSelector((state) => state.private.auth);
  const availableFactions = useSelector((state) => state.private.auth.availableFactions);
  const gameType = useSelector(selectGameType);
  const gameResults = useSelector(selectGameResults);
  const players = useSelector(selectPlayers);
  const startedAt = useSelector(selectGameStartedAt);
  const finalRound = useSelector(selectFinalRound);
  const gameEndReason = useSelector(selectGameEndReason);
  const gameEndRound = useSelector(selectGameEndRound);

  // В обучении итогов партии нет вовсе: главу завершает поздравление
  // (TutorialChapterCompleteModal). Флаг сюда обычно и не доходит — modalsMiddleware
  // его в обучении не ставит, — но кнопки меню и завершения фазы после конца
  // партии открывают итоги вручную, поэтому проверка и здесь
  const showModalFinal = modalShown && !combatResultsModalVisible && !cityCaptureModalVisible
    && !isFieldCutscenePlaying && !isTutorialActive;
  const localMatchSaveSentRef = useRef(false);

  // Ранговые образы, полученные ЭТИМ игроком за матч (награда высших лиг,
  // приходит в extendedGameResults вместе с нуменами и сундуками). Показываем
  // по одной модалке на образ поверх итогов матча — тем же приемом, что и
  // награды за главы обучения (см. TutorialChapterCompleteModal)
  const extendedGameResults = useSelector(selectExtendedGameResults);
  const ownRankSkins = useMemo(() => {
    const ownResult = (extendedGameResults || []).find((result) => result.id === auth?.id);
    return ownResult?.skins || [];
  }, [extendedGameResults, auth?.id]);

  // Торжество победителя — первым поверх итогов матча, раньше наград: итоги
  // отсортированы по месту (buildGameResults), первый — победитель
  const winnerResult = gameResults?.[0] || null;
  const winnerPlayer = winnerResult ? players.find((player) => player.id === winnerResult.id) || null : null;
  // Рейтинг победителя (рейтинговая партия) — из расширенных итогов сервера,
  // как и в плашке игрока в итогах; приходит чуть позже самих итогов
  const winnerExtendedResult = winnerResult
    ? (extendedGameResults || []).find((result) => result.id === winnerResult.id) || null
    : null;
  const [isWinnerModalShown, setIsWinnerModalShown] = useState(false);
  const [isWinnerModalClosed, setIsWinnerModalClosed] = useState(false);
  // Награды ждут, пока победителя поздравят (или показывать некого)
  const isWinnerCelebrationDone = isWinnerModalClosed || !winnerPlayer;

  useEffect(() => {
    if (!showModalFinal || !winnerPlayer || isWinnerModalShown || isWinnerModalClosed) {
      return undefined;
    }
    const timeoutId = setTimeout(() => setIsWinnerModalShown(true), RANK_SKIN_MODAL_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [showModalFinal, winnerPlayer, isWinnerModalShown, isWinnerModalClosed]);

  const handleWinnerModalClose = () => {
    setIsWinnerModalShown(false);
    setIsWinnerModalClosed(true);
  };

  const [claimedSkinsCount, setClaimedSkinsCount] = useState(0);
  const [isRankSkinModalShown, setIsRankSkinModalShown] = useState(false);
  const currentRankSkinId = ownRankSkins[claimedSkinsCount];

  // Награда за задание по итогам ЛОКАЛЬНОЙ партии: у нее нет ни лобби, ни
  // finalizeGame, поэтому задание закрывает сохранение итогов, а награда
  // приходит в ответе на него (см. POST /api/users/me/local-matches ниже)
  const [localMatchQuestReward, setLocalMatchQuestReward] = useState(null);

  // Награда за задание «сыграть матч в обычной сетевой игре»: сервер
  // начисляет ее при завершении матча и присылает в extendedGameResults
  // (см. finalizeGame). Показываем поверх итогов той же модалкой, что и за
  // главу обучения: фракцию — модалкой открытия фракции, сундук — своей.
  // Награда за бой с ботами показывается той же очередью
  const ownQuestReward = useMemo(() => {
    const ownResult = (extendedGameResults || []).find((result) => result.id === auth?.id);
    return ownResult?.questReward || localMatchQuestReward || null;
  }, [extendedGameResults, auth?.id, localMatchQuestReward]);

  const [isQuestRewardModalShown, setIsQuestRewardModalShown] = useState(false);
  const [isQuestRewardClaimed, setIsQuestRewardClaimed] = useState(false);
  const questRewardAppliedRef = useRef(false);

  // Локальный профиль обновляется сразу, без перезапроса: фракция появляется
  // в доступных, сундук зажигает метку «Новое» в коллекции.
  //
  // Сами задания здесь НЕ гасятся: раньше по факту любой награды помечалось
  // задание типа ONLINE_MATCH, и после первого рейтингового матча выполненным
  // локально становилось не то задание. Теперь их гасит обработчик результатов
  // матча по списку с сервера (см. LobbyPage) — он же ловит задания на лиги,
  // у которых наградной модалки нет вовсе
  useEffect(() => {
    if (!ownQuestReward || questRewardAppliedRef.current) {
      return;
    }
    questRewardAppliedRef.current = true;
    if (ownQuestReward.type === 'faction') {
      const nextFactions = (availableFactions || []).map((faction) => (
        faction.id === ownQuestReward.factionId ? {...faction, isAvailable: true} : faction
      ));
      dispatch(setAvailableFactions(nextFactions));
    }
    dispatch(updatePlayerRewards(0, undefined, true));
  }, [ownQuestReward, availableFactions, dispatch]);

  // Пауза перед наградной модалкой: игрок должен успеть увидеть итоги матча
  useEffect(() => {
    if (!showModalFinal || !currentRankSkinId || isRankSkinModalShown || !isWinnerCelebrationDone) {
      return undefined;
    }
    const timeoutId = setTimeout(() => setIsRankSkinModalShown(true), RANK_SKIN_MODAL_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [showModalFinal, currentRankSkinId, isRankSkinModalShown, isWinnerCelebrationDone]);

  const handleRankSkinClaim = () => {
    setIsRankSkinModalShown(false);
    setClaimedSkinsCount((count) => count + 1);
  };

  // Награда за задание показывается после ранговых образов, чтобы модалки не
  // накладывались
  useEffect(() => {
    if (!showModalFinal || !ownQuestReward || isQuestRewardClaimed || currentRankSkinId || !isWinnerCelebrationDone) {
      return undefined;
    }
    const timeoutId = setTimeout(() => setIsQuestRewardModalShown(true), RANK_SKIN_MODAL_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [showModalFinal, ownQuestReward, isQuestRewardClaimed, currentRankSkinId, isWinnerCelebrationDone]);

  const handleQuestRewardClose = () => {
    setIsQuestRewardModalShown(false);
    setIsQuestRewardClaimed(true);
  };

  // Переходы из наградной модалки: матч уже завершен, поэтому выходим из него
  // так же, как кнопкой «В главное меню»
  const leaveToScreen = (nextScreen) => {
    handleQuestRewardClose();
    onLeaveClick();
    setScreen(nextScreen);
  };

  useEffectOnPositiveChange(showModalFinal, () => {
    const soundManager = REGISTRY_SINGLETON.getSoundManager();
    soundManager.playModalRoundEnd();//TODO
  });

  // Итоги локальной партии сохраняются, как только партия кончилась, — не
  // дожидаясь, пока игрок закроет модалки последнего боя и захвата города и
  // увидит сами итоги: вкладку могут закрыть и раньше, а незакрытая запись
  // партии так и осталась бы в истории «не завершённой». В обучении итогов
  // нет вовсе (см. showModalFinal)
  const isLocalMatchSaveDue = modalShown && !isTutorialActive;

  useEffect(() => {
    if (!isLocalMatchSaveDue) return;
    if (localMatchSaveSentRef.current) return;
    if (gameType !== GAME_TYPES.LOCAL || !auth?.id || !gameResults || gameResults.length === 0) return;

    localMatchSaveSentRef.current = true;

    const finishedAt = Date.now();
    const durationMs = startedAt ? finishedAt - startedAt : 0;

    const payloadPlayers = gameResults.map((result) => {
      const player = players.find((p) => p.id === result.id);
      return {
        id: result.id,
        name: player?.name || '',
        color: result.color,
        factionId: player?.factionId || '',
        selectedSkin: player?.selectedSkin || '',
        finalWinPoints: result.finalWinPoints,
        isBot: Boolean(player?.isBot),
        // Пометка бота и уровень его бонуса: итоги перезаписывают состав матча
        // целиком, и без них значок бота на странице матча пропал бы
        isLocalBot: Boolean(player?.isLocalBot),
        bonus: player?.bonus || '',
        // Числа бонуса этой партии — по ним подсказка значка в истории
        ...(player?.bonusConfig ? {bonusConfig: player.bonusConfig} : {}),
      };
    });

    // Сохранение повторяется при сбоях сети и сервера и продолжается, даже
    // если игрок уже вышел из партии; если страница закроется раньше, итоги
    // дошлются при следующем входе (см. saveLocalMatchResults)
    saveLocalMatchResults({
      finishedAt,
      durationMs,
      finalRound,
      // Сколько раундов реально сыграли и почему партия закончилась досрочно
      endRound: gameEndRound,
      endReason: gameEndReason,
      // Партия писалась по ходу игры (см. localMatchReplayMiddleware): итоги
      // закрывают уже созданную запись, а не заводят второй матч в истории.
      // У партии без записи (миссия, режим зрителя) идентификатор придумываем
      // здесь же: по нему сервер узнает повтор запроса и не сохранит такую
      // партию дважды
      runId: getLocalMatchReplayRecorder()?.getRunInfo()?.runId ?? createDefaultRunId(),
      players: payloadPlayers,
    }, {userId: auth.id})
      .then(({data, attemptsCount}) => {
        if (!data) {
          return;
        }
        // Партию из одних ботов сервер в профиле не засчитывает вовсе: человек
        // ее только смотрел — ни счетчика локальных партий (по нему меню ведет
        // новичка по воронке), ни задания. Старый сервер поля не присылает —
        // тогда судим как прежде
        if (data.isMatchCounted === false) {
          return;
        }
        // Зачтен ли матч, решает сервер: isNewMatch. Но если ответ пришел не
        // с первой попытки и матч «не новый» — значит, сервер принял одну из
        // прошлых попыток, а ответ на нее потерялся: матч зачтен, просто
        // клиент об этом еще не знает (награду того сохранения сервер
        // возвращает и на повтор). С первой попытки «не новый» — итоги этой
        // партии уже сохранялись раньше, второй раз их не засчитываем
        const isMatchCountedNow = Boolean(data.isNewMatch) || attemptsCount > 1;
        if (!isMatchCountedNow) {
          return;
        }
        const questReward = applyLocalMatchSaveResult(data, dispatch, {isMatchCountedNow});
        if (questReward) {
          // Наградная модалка — только пока игрок еще на итогах партии
          setLocalMatchQuestReward(questReward);
        }
      })
      .catch((error) => {
        console.error('Failed to apply local match save result:', error);
      });
  }, [
    isLocalMatchSaveDue, gameType, auth?.id, gameResults, players, startedAt, finalRound, gameEndRound,
    gameEndReason, dispatch,
  ]);

  if (!showModalFinal || !gameResults) {
    return null;
  }

  const handleHide = () => {
    dispatch(hideGameEndModal());
  }

  const onLeaveClick = () => {
    closeNetworkSocket();
    dispatch(leaveLobby());
    dispatch(resetState());
  }

  return (
    <>
    <ModalWindow
      handleClose={handleHide}
      className={'game-end-modal'}
      showQuote={true}
    >
      <div className="game-end-modal-header">
        <h2 className="text-controls">
          Игра завершена!
        </h2>
        {/* Раньше последнего раунда партию закончил отрыв лидера в очках */}
        {gameEndReason === GAME_END_REASONS.WIN_POINTS_GAP && (
          <div className="game-end-modal-early-end-note">
            Игра завершена досрочно{gameEndRound ? ` в конце ${gameEndRound} раунда` : ''}: лидер опередил каждого соперника на {GAME_END_WIN_POINTS_GAP} {pluralizeRu(GAME_END_WIN_POINTS_GAP, 'очко', 'очка', 'очков')}
          </div>
        )}
        {/* Раньше последнего раунда партию закончили выбывшие соперники */}
        {gameEndReason === GAME_END_REASONS.LAST_PLAYER_STANDING && (
          <div className="game-end-modal-early-end-note">
            Игра завершена досрочно{gameEndRound ? ` в конце ${gameEndRound} раунда` : ''}: у всех соперников лидера не осталось ни отрядов, ни городов, а очков у него больше, чем у каждого из них
          </div>
        )}
      </div>

      <GameEndContent/>

      <div className={'game-end-modal-footer'}>
        <ModalButton
          onClick={() => {
            onLeaveClick();
            if (isRanked && handleRankedGame && !isGuest) {
              handleRankedGame();
            } else if (gameType === GAME_TYPES.LOCAL) {
              setScreen(SCREENS.FIELD_SELECT);
            } else {
              setScreen(SCREENS.LOBBY_LIST);
            }
          }}
          text={'Снова в битву!'}
          style={{
            backgroundColor: PRIMARY_COLOR
          }}
        />
        <ModalButton
          onClick={() => {
            onLeaveClick();
            setScreen(SCREENS.MENU);
          }}
          text={'В главное меню'}
        />
        <ModalButton
          onClick={handleHide}
          text={'Скрыть окно'}
        />
      </div>
    </ModalWindow>

      {isWinnerModalShown && winnerPlayer && (
        <MatchWinnerModal
          player={winnerPlayer}
          finalWinPoints={winnerResult.finalWinPoints}
          isRanked={isRanked}
          ratingDelta={winnerExtendedResult?.ratingDelta}
          rating={winnerExtendedResult?.rating}
          onClose={handleWinnerModalClose}
        />
      )}

      {isRankSkinModalShown && currentRankSkinId && (
        <RankSkinRewardModal
          key={currentRankSkinId}
          skinId={currentRankSkinId}
          onClaim={handleRankSkinClaim}
        />
      )}

      {isQuestRewardModalShown && ownQuestReward?.type === 'faction' && (
        <FactionPurchaseRewardModal
          factionId={ownQuestReward.factionId}
          onClaim={handleQuestRewardClose}
        />
      )}

      {isQuestRewardModalShown && ownQuestReward?.type === 'chest' && (
        <QuestChestRewardModal
          chestId={ownQuestReward.chestId}
          onOpenInventory={() => leaveToScreen(SCREENS.INVENTORY)}
          onMoreChests={() => leaveToScreen(SCREENS.SHOP)}
          onClose={handleQuestRewardClose}
        />
      )}
    </>
  );
};

export default GameEndModal;
